<template>
  <router-view/>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import router from '@/router'
import store from '@/store'
import http from '@/utils/request'

const SESSION_TIMEOUT_KEY = 'sessionTimeoutMinutes'
const SESSION_EXPIRED_KEY = 'sessionExpiredByIdle'
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove']

let inactivityTimer: number | undefined
let lastResetAt = 0


function getToken() {
  return localStorage.getItem('token')
}


function clearInactivityTimer() {
  if (inactivityTimer) {
    window.clearTimeout(inactivityTimer)
    inactivityTimer = undefined
  }
}


function getTimeoutMilliseconds() {
  const timeoutMinutes = Number(localStorage.getItem(SESSION_TIMEOUT_KEY) || '60')
  return Math.max(timeoutMinutes, 1) * 60 * 1000
}


function markSessionExpiredByIdle() {
  localStorage.setItem(SESSION_EXPIRED_KEY, '1')
  clearInactivityTimer()
}


function resetInactivityTimer(force = false) {
  if (!getToken()) {
    clearInactivityTimer()
    return
  }

  const now = Date.now()
  if (!force && now - lastResetAt < 1000) {
    return
  }

  lastResetAt = now
  clearInactivityTimer()
  inactivityTimer = window.setTimeout(() => {
    markSessionExpiredByIdle()
  }, getTimeoutMilliseconds())
}


async function redirectToLoginByIdle() {
  localStorage.removeItem(SESSION_EXPIRED_KEY)
  await store.dispatch('Users/logout')
  ElMessage.error('会话因长时间未操作已失效，请重新登录')

  if (router.currentRoute.value.path !== '/login') {
    router.replace('/login')
  }
}


function handleIdleLogout() {
  if (!getToken() || localStorage.getItem(SESSION_EXPIRED_KEY) !== '1') {
    return false
  }

  clearInactivityTimer()
  void redirectToLoginByIdle()
  return true
}


function handleUserActivity() {
  if (handleIdleLogout()) {
    return
  }

  if (getToken()) {
    resetInactivityTimer()
  }
}


async function loadSessionTimeoutSettings(force = false) {
  if (!getToken()) {
    return
  }

  if (!force && localStorage.getItem(SESSION_TIMEOUT_KEY)) {
    resetInactivityTimer(true)
    return
  }

  try {
    const res = await http.get('/api/system/session-settings')
    if (res.data.status === 200) {
      localStorage.setItem(
        SESSION_TIMEOUT_KEY,
        String(res.data.data.sessionTimeoutMinutes)
      )
      resetInactivityTimer(true)
    }
  } catch (error) {
    void error
  }
}


function trackPageAccess() {
  const currentRoute = router.currentRoute.value

  if (!getToken() || currentRoute.path === '/login') {
    return
  }

  http.post('/api/system/log-page-access', {
    pagePath: currentRoute.fullPath,
    pageName: typeof currentRoute.name === 'string' ? currentRoute.name : ''
  }).catch(() => {
    return
  })
}


onMounted(() => {
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleUserActivity, true)
  })

  router.afterEach(() => {
    if (handleIdleLogout()) {
      return
    }

    loadSessionTimeoutSettings()
    resetInactivityTimer(true)
    trackPageAccess()
  })

  if (getToken()) {
    loadSessionTimeoutSettings(true)
    resetInactivityTimer(true)
    trackPageAccess()
  }
})


onUnmounted(() => {
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.removeEventListener(eventName, handleUserActivity, true)
  })
  clearInactivityTimer()
})
</script>

<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: #42b983;
    }
  }
}
</style>
